import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import ContentfulPreOrderModal from '@src/quince-site-common/src/domains/contentful/ContentfulPreOrder'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOption } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { getIsSelectedColorAppExclusive } from '@src/quince-site-common/src/helpers/productHelper'
import {
  getOverriddenOfferMessage,
} from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'

import Link from '../link'
import { CTA_TYPE } from '../productCTA/productCTAConstants'
import ProductMessage from '../productCTA/productMessage'
import { ProductCustomBannerMessage } from '../productCustomBannerMessage'
import { documentToReactComponentsTextOptions, VariantPurchaseInfo } from '../productForm'
import styles from '../productForm/productForm.module.scss'
import { default as ProductOfferMessage } from '../productOfferMessage'

// @i18n-ignore
const QUINCE_APP = 'Quince App'

export interface GreyBoxesProps {
  product?: Product
  productOptions?: ProductOption[]
  selectedOptions?: ProductVariantOption[]
  selectedVariantPurchaseInfo?: VariantPurchaseInfo
  productCTAType: CTA_TYPE
  isReturnable?: boolean
  subDescriptionText?: string
  hideOfferMessage?: boolean
  offerMessageFromPC?: string
  hideMtoText?: boolean
  preOrderModalData?: ContentfulPreOrderModal
  showNewPdpDesignV2?: boolean
  isFinalSaleProduct?: boolean
  handleShopInApp: ({
    product,
    selectedOptions,
  }: {
    product?: Product
    selectedOptions?: ProductVariantOption[]
  }) => void
}

const GreyBoxes = (props: GreyBoxesProps) => {
  const { t } = useTranslation('common')
  const { productCTAType } = props

  const isColorAppExclusive = getIsSelectedColorAppExclusive(props.productOptions, props.selectedOptions)

  const offerMessage = getOverriddenOfferMessage(props.offerMessageFromPC, props.product, props.selectedOptions)

  const shouldShowMTOText =
    !props.hideMtoText &&
    props.selectedVariantPurchaseInfo?.showMadeToOrder &&
    props.selectedVariantPurchaseInfo.madeToOrderText

  const shippingInfoText = props.product && productHelper.getShippingInfoText(props.product)

  const showCustomBannerMessage = props.showNewPdpDesignV2
    ? !productHelper.getIsAgeRestrictedProduct(props.product as Product)
    : true

  const hasMtoData = props.selectedVariantPurchaseInfo?.madeToOrderText ?? shippingInfoText

  // If the product is app exclusive, show only app exclusive message and the offer message
  if (isColorAppExclusive) {
    return (
      <>
        <div
          className={classnames(styles.container__subDescText, styles.container__subDescText_adjustedMargin, {
            [styles.container__subDescText_newDesignV2 ?? '']: props.showNewPdpDesignV2,
          })}
        >
          {t('app_exclusive_available')}
          {` `}
          <Link
            onClick={() => {
              props.handleShopInApp({ product: props.product, selectedOptions: props.selectedOptions })
            }}
            className={classnames(styles.container__subDescText__und_offset, 'text-decor--underline d-inline')}
          >
            {QUINCE_APP}
          </Link>
          {`. `}
          {t('app_exclusive_early_access')}
        </div>
        <ProductOfferMessage
          hideOfferMessage={props.hideOfferMessage}
          offerMessageFromPC={offerMessage}
          productId={props.product?.spid as number}
          showFinalSaleMessage={props.isFinalSaleProduct}
          showNewPdpDesignV2={props.showNewPdpDesignV2}
        />
      </>
    )
  }

  return (
    <>
      {(shouldShowMTOText ||
        (!productHelper.getIsAgeRestrictedProduct(props.product as Product))) && (
        <div
          className={classnames(styles.container__mtoText, {
            [styles.container__mtoText_newDesignV2 ?? '']: props.showNewPdpDesignV2,
            [styles.container__mtoText_newDesignV2_hidden ?? '']: !hasMtoData && props.showNewPdpDesignV2,
          })}
        >
          {shouldShowMTOText ? (  
            <>
              {props.selectedVariantPurchaseInfo?.madeToOrderText
                ? documentToReactComponents(
                    props.selectedVariantPurchaseInfo.madeToOrderText,
                    documentToReactComponentsTextOptions,
                  )
                : null}
            </>
          ) : (
            shippingInfoText && (
              //eslint-disable-next-line
              <div dangerouslySetInnerHTML={{ __html: getSanitizedHtml(shippingInfoText as string) }} />
            )
          )}
          { !props.showNewPdpDesignV2 && (
            <div
              className={classnames(styles.container__mtoText__subText, 'd-block', {
                [styles.container__mtoText__subText_newDesignV2 ?? '']: props.showNewPdpDesignV2,
              })}
            >
              {t('this_product_is_not_returnable')}
            </div>
          )}
        </div>
      )}
      {props.subDescriptionText && (
        <div
          className={classnames(styles.container__subDescText, styles.container__subDescText_adjustedPadding, {
            [styles.container__subDescText_newDesignV2 ?? '']: props.showNewPdpDesignV2,
          })}
          data-testid="sub-description-text"
        >
          {
            <div
              className={classnames(styles.container__subDescText__subText, {
                [`${styles.container__subDescText__textColorBlack}`]: props.isReturnable,
              })}
              //eslint-disable-next-line
              dangerouslySetInnerHTML={{ __html: getSanitizedHtml(props.subDescriptionText) }}
            />
          }
        </div>
      )}

      <ProductOfferMessage
        hideOfferMessage={props.hideOfferMessage}
        offerMessageFromPC={offerMessage}
        productId={props.product?.spid as number}
        showFinalSaleMessage={props.isFinalSaleProduct}
        showNewPdpDesignV2={props.showNewPdpDesignV2}
      />
      <ProductMessage
        type={productCTAType}
        selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
        preOrderModalData={props.preOrderModalData}
        showNewPdpDesignV2={props.showNewPdpDesignV2}
      />
      {showCustomBannerMessage && (
        <ProductCustomBannerMessage
          customBannerMessageFromPC={productHelper.getPDPCustomBannerText(props.product)}
          productId={props.product?.spid as number}
        />
      )}
    </>
  )
}

export default GreyBoxes
